import { ChatIcon, WorkIcon } from "@/components/icons";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { ComponentType } from "react";

import { SHELL_TEXTS } from "./texts.ts";
import {
  CHAT_MODE_ENABLED,
  MODE_LABELS,
  WORKSPACE_MODES,
  type WorkspaceMode,
} from "./workspace.ts";

/**
 * El icono de cada modo.
 *
 * Vive aquí y no en `workspace.ts` porque aquello es la tabla que traduce
 * entre modo y ruta --datos-- y esto es cómo se dibuja. El icono acompaña al
 * texto, nunca lo sustituye: el conmutador se lee, no se adivina.
 */
const MODE_ICONS: Record<
  WorkspaceMode,
  ComponentType<{ className?: string }>
> = {
  chat: ChatIcon,
  work: WorkIcon,
};

/**
 * El conmutador entre los dos modos (§9.2).
 *
 * Es el único mueble que NO cambia al cambiar de modo, y por eso ocupa el
 * mismo sitio en los dos armazones: es así como el usuario aprende que desde
 * arriba se cambia de sitio.
 *
 * Los dos armazones montan este mismo componente en vez de dibujar cada uno el
 * suyo, porque dos copias del mismo mueble se separan al primer retoque.
 *
 * Con el modo Chat apagado (`CHAT_MODE_ENABLED`) no se dibuja: solo queda
 * Work, y un conmutador de un solo modo no conmuta nada. Al ser el mismo
 * componente en todas partes, se apaga en todas a la vez.
 */
export function ModeSwitch({
  mode,
  onMode,
}: {
  readonly mode: WorkspaceMode;
  onMode: (mode: WorkspaceMode) => void;
}) {
  if (!CHAT_MODE_ENABLED) return null;
  return (
    <Tabs
      value={mode}
      onValueChange={(value) => {
        onMode(value as WorkspaceMode);
      }}
    >
      <TabsList aria-label={SHELL_TEXTS.workModeLabel}>
        {WORKSPACE_MODES.map((name) => {
          const Icon = MODE_ICONS[name];
          return (
            <TabsTrigger key={name} value={name}>
              <Icon className="size-4" aria-hidden />
              {MODE_LABELS[name]}
            </TabsTrigger>
          );
        })}
      </TabsList>
    </Tabs>
  );
}
